import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useState, type ReactNode, type RefObject} from "react";
import {ArchiveConfirmationModal} from "~/components/archiveConfirmationModal";
import {getArchiveDisabledReasonFromRules} from "~/components/eventArchiveAction";
import {invalidateDataViewerQueriesAfterJourneyArchive} from "~/components/ottopilotDataViewer/invalidateOttopilotDataViewerQueries";
import {dismissDataViewerRowEngagementPanel} from "~/components/ottopilotDataViewer/rowEngagement/dismissDataViewerRowEngagementPanel";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {useOttopilotConfiguration} from "~/contexts/ottopilotConfigurationContext";
import {useWorkspace} from "~/contexts/workspaceContext";
import {useWorkspaceUser} from "~/contexts/workspaceUserContext";
import {useEventTemplates} from "~/contexts/workspaceEventTemplatesContext";
import {useEventContainerTemplates} from "~/contexts/workspaceEventContainerTemplatesContext";
import {useAnankeFlags} from "@vesta/ananke/hooks/useAnankeFlags";
import {archiveEventContainerLikeArchiveDropdown} from "~/utilities/archiveEventContainerWithEvents";
import type {EventContainerWithEvents} from "~/utilities/apis/api";

export function useEventContainerArchiveEngagement({
    queryKeyPrefix,
    controllerRef,
}: {
    queryKeyPrefix: Array<unknown>;
    controllerRef?: RefObject<OttopilotDataViewerController<EventContainerWithEvents> | null>;
}) {
    const [archiveTarget, setArchiveTarget] = useState<EventContainerWithEvents | null>(null);
    const {heimdallUser} = useWorkspaceUser();
    const {intellsysBaseUrl} = useOttopilotConfiguration();
    const {workspace, workspaceCapabilities} = useWorkspace();
    const queryClient = useQueryClient();
    const eventContainerTemplates = useEventContainerTemplates();
    const eventTemplates = useEventTemplates();
    const {showFlag} = useAnankeFlags();

    const archiveTargetTemplate =
        archiveTarget === null
            ? null
            : eventContainerTemplates.find(
                  (template) => template.id === archiveTarget.templateId,
              ) ?? null;
    const statusColumnConfig =
        archiveTargetTemplate?.configuration.statusColumnConfig;
    const isArchiveAvailable = eventContainerTemplates.some(
        (template) => template.configuration.statusColumnConfig !== undefined,
    );

    const getArchiveDisabledReason = (eventContainer: EventContainerWithEvents): string | null => {
        const eventContainerTemplate = eventContainerTemplates.find(
            (template) => template.id === eventContainer.templateId,
        );
        if (eventContainerTemplate?.configuration.statusColumnConfig === undefined) {
            return "Archive is not available";
        }
        return getArchiveDisabledReasonFromRules({
            rules: eventContainerTemplate.configuration.archiveConfig?.rules ?? [],
            additionalColumnValues: eventContainer.additionalColumnValues,
        });
    };

    const archiveMutation = useMutation({
        mutationFn: async () => {
            if (archiveTarget === null || statusColumnConfig === undefined) {
                throw new Error("Status column configuration not found for Event Containers");
            }

            const result = await archiveEventContainerLikeArchiveDropdown({
                accessToken: heimdallUser.accessToken,
                intellsysBaseUrl: intellsysBaseUrl,
                eventContainer: archiveTarget,
                workspaceId: workspace.id,
                workspace: workspace,
                workspaceCapabilities,
                statusColumnConfig: statusColumnConfig,
                eventTemplates: eventTemplates,
            });

            if (result.success !== true) {
                throw result.error;
            }
        },
        onSuccess: () => {
            showFlag({
                title: "Event Container archived",
                description: "The Event Container and its events were archived successfully.",
                appearance: "success",
                isAutoDismiss: true,
                autoDismissSeconds: 3,
            });
            setArchiveTarget(null);
            dismissDataViewerRowEngagementPanel(controllerRef);
            void invalidateDataViewerQueriesAfterJourneyArchive(
                queryClient,
                queryKeyPrefix,
                workspace.id,
            );
        },
        onError: (error: unknown) => {
            showFlag({
                title: "Archive failed",
                description:
                    error instanceof Error ? error.message : "Could not archive the Event Container. Please try again.",
                appearance: "error",
                isAutoDismiss: true,
                autoDismissSeconds: 4,
            });
        },
    });

    const requestArchive = (eventContainer: EventContainerWithEvents) => {
        const disabledReason = getArchiveDisabledReason(eventContainer);
        if (disabledReason !== null) {
            showFlag({
                title: "Cannot Archive",
                description: disabledReason,
                appearance: "warning",
                isAutoDismiss: true,
                autoDismissSeconds: 4,
            });
            return;
        }
        setArchiveTarget(eventContainer);
    };

    const archiveModals: ReactNode =
        archiveTarget !== null && isArchiveAvailable === true ? (
            <ArchiveConfirmationModal
                isOpen={true}
                setIsOpen={(open) => {
                    if (open === false) {
                        setArchiveTarget(null);
                    }
                }}
                itemType="journey"
                onConfirm={async () => {
                    await archiveMutation.mutateAsync();
                }}
                isLoading={archiveMutation.isPending}
            />
        ) : null;

    const dismissArchive = () => {
        setArchiveTarget(null);
    };

    return {
        isArchiveAvailable,
        getArchiveDisabledReason,
        requestArchive,
        dismissArchive,
        archiveModals,
        archiveTargetEventContainerId: archiveTarget?.id ?? null,
    };
}
